import React from 'react';
import {useFormBuilderContext} from 'src/app/form-builder/context/FormBuilderContext';
import type {IFormFieldV2, IFormSettings} from 'src/types/FormV2';

export const useGetFieldList = (): IFormFieldV2[] => {
  const {state} = useFormBuilderContext();
  return state.fieldList;
};

export const useUpdateFieldList = (): ((fieldList: IFormFieldV2[]) => void) => {
  const {dispatch} = useFormBuilderContext();

  const updateFieldListFn = React.useCallback(
    (fieldList: IFormFieldV2[]): void => {
      dispatch({type: 'updateFieldList', payload: fieldList});
    },
    [dispatch]
  );

  return updateFieldListFn;
};

export const useGetFormSettings = (): IFormSettings => {
  const {state} = useFormBuilderContext();
  return state.formSettings;
};

export const useUpdateFormSettings = (): ((
  formSettings: IFormSettings
) => void) => {
  const {dispatch} = useFormBuilderContext();

  const updateFormSettingsFn = (formSettings: IFormSettings): void => {
    dispatch({type: 'updateFormSettings', payload: formSettings});
  };

  return updateFormSettingsFn;
};

export const useLoadFormSettings = (): ((
  formSettings: IFormSettings
) => void) => {
  const {dispatch} = useFormBuilderContext();

  const loadFormSettingsFn = (formSettings: IFormSettings): void => {
    dispatch({type: 'loadFormSettings', payload: formSettings});
  };

  return loadFormSettingsFn;
};

export const useGetSettingsSeed = (): number => {
  const {state} = useFormBuilderContext();
  return state.settingsSeed;
};

// Field selection by ID
export const useGetSelectedFieldId = (): string | null => {
  const {state} = useFormBuilderContext();
  return state.selectedFieldId;
};

export const useUpdateSelectedFieldId = (): ((
  fieldId: string | null
) => void) => {
  const {dispatch} = useFormBuilderContext();

  const updateSelectedFieldIdFn = (fieldId: string | null): void => {
    dispatch({type: 'updateSelectedFieldId', payload: fieldId});
  };

  return updateSelectedFieldIdFn;
};

export const useGetSelectedField = (): IFormFieldV2 | undefined => {
  const fieldList = useGetFieldList();
  const selectedFieldId = useGetSelectedFieldId();

  if (!selectedFieldId) return undefined;
  return fieldList.find(field => field.invariantId === selectedFieldId);
};

export const useUpdateSelectedField = (): ((field: IFormFieldV2) => void) => {
  const selectedFieldId = useGetSelectedFieldId();
  const fieldList = useGetFieldList();
  const updateFieldList = useUpdateFieldList();

  const updateSelectedFieldFn = React.useCallback(
    (field: IFormFieldV2): void => {
      if (!selectedFieldId) return;

      const index = fieldList.findIndex(f => f.invariantId === selectedFieldId);
      if (index === -1) return;

      const newFieldList = [...fieldList];
      newFieldList[index] = field;
      updateFieldList(newFieldList);
    },
    [selectedFieldId, fieldList, updateFieldList]
  );

  return updateSelectedFieldFn;
};

// Form validation states
export const useGetFieldSettingsValid = (): boolean => {
  const {state} = useFormBuilderContext();
  return state.isFieldSettingsValid;
};

export const useUpdateFieldSettingsValid = (): ((isValid: boolean) => void) => {
  const {dispatch} = useFormBuilderContext();

  const updateFieldSettingsValidFn = (isValid: boolean): void => {
    dispatch({type: 'setFieldSettingsValid', payload: isValid});
  };

  return updateFieldSettingsValidFn;
};

export const useResetFormBuilderState = (): (() => void) => {
  const {dispatch} = useFormBuilderContext();

  const resetFormBuilderStateFn = React.useCallback((): void => {
    dispatch({type: 'resetFormBuilderState'});
  }, [dispatch]);

  return resetFormBuilderStateFn;
};
